import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { prepareTableData } from "../../../../features/dataFormatFunctions/dataFormatter";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import LoaderWrapper from "../../../../widgets/loaderWrapper/LoaderWrapper";
import { configureTableLazyState, defineTableFilters, TableEvents, tableStateModifyHandle } from "../../../../widgets/table/tableFeatures/tableFunctions";
import { getBasicTablePaginator } from "../../../../widgets/table/tableFeatures/tableTemplates/tablePaginatorTemplates";
import TableWidget from "../../../../widgets/table/tableWidget/TableWidget";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { authorizeToCustomRest } from "../../../../features/customAuth/customAuth";
import { defineCustomTableColumns, fetchCustomTableObjectTypeDataCount, lazyFetchCustomTableData, prepareQueryArgsForCustomTableFetch, saveCustomTableStateWebStorage, defineOnRowSelectFunction, defineRowIsSelectableAndRowClassName } from "./customTableObjectTypeFunctions";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";
import { showToastMessage } from "../../../../stateManagement/reducers";
import { useDispatch } from "react-redux";
import ToastMessageContainer from "../../../../widgets/toastMessageContainer/ToastMessageContainer";

export default function CustomTableObjectType() {
    const { id, objectType }: any = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [name, setName] = useState<any>(null)
    const [tableState, setTableState] = useState<any>(null)
    const [tableDataIsLoading, setTableDataIsLoading] = useState<boolean>(true)
    const [isErrorTableStructure, setIsErrorTableStructure] = useState<boolean>(false)
    const [isErrorTableData, setIsErrorTableData] = useState<boolean>(false)
    const [tableData, setTableData] = useState<any>([])
    const [dataCount, setDataCount] = useState<number>(0)
    const [onRowSelect, setOnRowSelect] = useState<any>(null)
    const [rowClassName, setRowClassName] = useState<any>(null)
    const [rowIsSelectable, setRowIsSelectable] = useState<any>(null)
    const [columns, setColumns] = useState<any>(null)
    const [paginator, setPaginator] = useState<any>({})
    const [paginatorPageInputTooltip, setPaginatorPageInputTooltip] = useState<any>('Введите номер страницы.')
    const restAuthToken = useAppSelector(state => state.customAuth.customAuthToken)
    const currentConfig = getItemWebStorage('currentConfig')
    const { tableAdditionalItems } = currentConfig
    const tableAdditionalItem = tableAdditionalItems.find((item: any) => item?.id === id)

    useEffect(() => {
        const { title, onRowSelectOptions, openDataOptions: restAuthRequestOptions } = tableAdditionalItem ?? {}
        if (restAuthRequestOptions && !restAuthToken) {
            authorizeToCustomRest(restAuthRequestOptions)
        }
        title ? setName(`Таблица «${title}»`) : setName('Таблица «...»')
        if (onRowSelectOptions) {
            const onRowSelect = () => defineOnRowSelectFunction(id, objectType, onRowSelectOptions, tableAdditionalItem, navigate)
            setOnRowSelect(onRowSelect)
            const { propertyForEnabledCheck, propertyForEnabledCheckType } = onRowSelectOptions ?? {}
            if (propertyForEnabledCheck) {
                const { rowIsSelectable, rowClassName } = defineRowIsSelectableAndRowClassName(propertyForEnabledCheck, propertyForEnabledCheckType)
                setRowIsSelectable(() => rowIsSelectable)
                setRowClassName(() => rowClassName)
            }
        }
    }, [objectType])

    async function defineTableColumns() {
        const { structure, openDataOptions: restAuthRequestOptions, api } = tableAdditionalItem ?? {}
        if (structure) {
            let columnsStructure: any = structure?.columns
            let columns = await defineCustomTableColumns(columnsStructure, api, restAuthToken, restAuthRequestOptions)
            let filters = defineTableFilters(columns)
            setColumns(columns)
            setTableState(getItemWebStorage("customTablesState")?.[objectType] ?? configureTableLazyState({ filters }))
        }
        else {
            setIsErrorTableStructure(true)
        }
    }

    async function getTableValueAndCount() {
        setTableDataIsLoading(true)
        const { openDataOptions: restAuthRequestOptions, api } = tableAdditionalItem ?? {}
        const { data: tableDataApi, dataCount: tableDataCountApi } = api ?? {}
        const { sort, filter, offset }: any = prepareQueryArgsForCustomTableFetch(tableState, columns)
        let { tableData, error }: any = await lazyFetchCustomTableData(tableDataApi, restAuthToken, 100, offset, sort, filter, restAuthRequestOptions)
        if (error) {
            setIsErrorTableData(true)
        }
        tableData = prepareTableData(tableData, columns)
        setTableData(tableData)
        let dataCount = await fetchCustomTableObjectTypeDataCount(tableDataCountApi, restAuthToken, filter, restAuthRequestOptions)
        setDataCount(dataCount)
        setTableDataIsLoading(false)
    }

    useEffect(() => {
        if (restAuthToken) {
            defineTableColumns()
        }
    }, [restAuthToken])

    useEffect(() => {
        if (restAuthToken && columns) {
            getTableValueAndCount()
        }
    }, [restAuthToken, columns, tableState])

    // const showTableInfo = useAppSelector(state => state.table.showTableInfo)

    useEffect(() => {
        const paginatorTemplate = getBasicTablePaginator(paginatorPageInputTooltip, setPaginatorPageInputTooltip, tableState, setTableState, dataCount, objectType)
        setPaginator(paginatorTemplate)
    }, [dataCount, tableState, paginatorPageInputTooltip])

    const onSort = (state: any) => tableStateModifyHandle(state, TableEvents.sort, setTableState, objectType)
    const onPage = (state: any) => tableStateModifyHandle(state, TableEvents.page, setTableState, objectType)
    const onFilter = (state: any) => tableStateModifyHandle(state, TableEvents.filter, setTableState, objectType)

    useEffect(() => {
        if (isErrorTableStructure || isErrorTableData) {
            setTableDataIsLoading(false)
        }
    }, [isErrorTableStructure, isErrorTableData])

    useEffect(() => {
        if (isErrorTableStructure || isErrorTableData) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [isErrorTableStructure, isErrorTableData])

    return (
        <>
            <ContentHeader title={name} />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer/>
                <LoaderWrapper isLoading={tableDataIsLoading} >
                    <TableWidget
                        tableValue={tableData}
                        tableState={tableState}
                        columns={columns}
                        onRowSelect={onRowSelect}
                        dataCount={dataCount}
                        paginatorTemplate={paginator}
                        pageSize={100}
                        onPage={onPage}
                        onSort={onSort}
                        onFilter={onFilter}
                        rowIsSelectable={rowIsSelectable}
                        rowClassName={rowClassName}
                        type="custom"
                        isLazy={true}
                    />
                </LoaderWrapper>
            </div >
        </>
    )
}